/**
 * An approved change on its way out of Lighthouse (archiving 4,000 threads in Gmail, syncing a calendar): what to
 * say while it runs, once it's done, and when some of it didn't go through. Pure, so the card and the tests share it.
 *
 * The numbers come from `GET /changesets/:id/progress` (ChangesetProgress), polled while `state` is `applying`.
 */
import type { ChangesetProgress } from './api'

/** More lines (or effects) than this and a change is "big": a digest instead of its lines, and a progress card. */
export const BULK_LINES = 30
/** A small change still gets a progress card once it has been running this long. */
export const SLOW_AFTER_MS = 2500

export type ProgressTone = 'live' | 'done' | 'warn' | 'error'

export interface ProgressView {
    tone: ProgressTone
    title: string
    /** "1,240 of 4,465 · about 2 min left" */
    detail: string
    /** 0–100, for the bar. */
    pct: number
    /** A line under the bar: why it's paused, or what was skipped. */
    note: string
}

const n = (x: number) => x.toLocaleString('en-US')
const plural = (count: number, one: string, many = `${one}s`) => `${n(count)} ${count === 1 ? one : many}`

/** "a few seconds left", "about 2 min left", "about 1 hr left". */
export function etaLabel(seconds: number | null | undefined): string {
    if (seconds == null || !Number.isFinite(seconds)) return ''
    if (seconds < 20) return 'a few seconds left'
    if (seconds < 90) return 'about a minute left'
    if (seconds < 3600) return `about ${Math.round(seconds / 60)} min left`
    const h = Math.round(seconds / 360) / 10
    return `about ${h} hr left`
}

export function progressPercent(p: Pick<ChangesetProgress, 'finished' | 'total'>): number {
    if (!p.total) return 0
    return Math.min(100, Math.round((p.finished / p.total) * 100))
}

export const isSettled = (p: Pick<ChangesetProgress, 'state'> | null | undefined) =>
    !!p && p.state !== 'applying'

/**
 * Whether the card shows progress at all: always when something failed; while running, for a big change or one that
 * has been running a while (a 1-second change just shows its receipt); once done, only for a big change.
 */
export function showsProgress(p: ChangesetProgress | null | undefined, opts: { bulk: boolean; slow: boolean }): boolean {
    if (!p || !p.total || p.state === 'none' || p.state === 'cancelled') return false
    if (p.failed > 0) return true
    if (p.state === 'applying') return opts.bulk || opts.slow || p.total > BULK_LINES
    return opts.bulk || p.total > BULK_LINES
}

export function progressView(p: ChangesetProgress): ProgressView {
    const pct = progressPercent(p)
    const why = p.skips[0]?.reason.replace(/\.$/, '')
    const skippedNote = p.skipped
        ? `${plural(p.skipped, 'change')} skipped${why ? `: ${why[0].toLowerCase()}${why.slice(1)}` : ''}.`
        : ''
    if (p.state === 'applying') {
        const paused = p.waiting > 0 && p.running === 0 && p.queued === p.waiting
        const eta = etaLabel(p.eta_s)
        return {
            tone: 'live',
            title: p.finished ? 'Making the changes' : 'Starting the changes',
            detail: `${n(p.finished)} of ${n(p.total)}${eta ? ` · ${eta}` : ''}`,
            pct,
            note: paused
                ? 'Pausing for the provider’s rate limit. It picks up again on its own.'
                : 'This keeps going if you leave.',
        }
    }
    const putBack = p.reverted
        ? p.reverted >= p.failed
            ? `Lighthouse undid ${p.failed === 1 ? 'it' : 'them'} on its side too, so nothing is out of sync.`
            : `Lighthouse undid ${n(p.reverted)} of them on its side too, so they're not out of sync.`
        : ''
    if (p.state === 'failed') {
        return {
            tone: 'error',
            title: p.total === 1 ? "That change didn't go through" : `None of the ${n(p.total)} changes went through`,
            detail: p.failures[0]?.reason ?? '',
            pct: 100,
            note: putBack,
        }
    }
    if (p.state === 'partial') {
        return {
            tone: 'warn',
            title: `${plural(p.failed, 'change')} couldn't be made`,
            detail: `${n(p.done + p.skipped)} of ${n(p.total)} done`,
            pct: 100,
            note: [putBack, skippedNote].filter(Boolean).join(' '),
        }
    }
    return {
        tone: 'done',
        title: p.total === 1 ? 'Done' : `All ${n(p.total)} changes made`,
        detail: '',
        pct: 100,
        note: skippedNote,
    }
}

/**
 * Sent on `window` when an approved change finished changing the creator's mailbox, so an open inbox re-reads
 * what only the provider knows (Gmail's unread count beside the Inbox tab), which realtime doesn't carry.
 */
export const MAILBOX_CHANGED_EVENT = 'lh:mailbox-changed'

/** How long to wait before asking again: quick while it moves, slower in a background tab or after an error. */
export function pollDelay(opts: { hidden: boolean; errors: number }): number {
    if (opts.errors) return Math.min(15000, 2000 * 2 ** Math.min(opts.errors, 3))
    return opts.hidden ? 6000 : 1500
}
